import { MessageStatus, OutreachMessageDto } from "@growth/dto";
import { Badge, Button, cn, ConfirmDialog, Field, Input, PromptDialog, Textarea } from "@growth/ui";
import { ArrowLeft, FlaskConical, LoaderCircle, Save, Send, Trash2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { formatDate } from "../../lib/format";
import {
  useDeleteDraft,
  useLeadPeople,
  useLeadPerson,
  useOutreach,
  useOutreachMessage,
  useSendBack,
  useSendingSettings,
  useSendMessage,
  useUpdateMessage,
} from "../../lib/leads";
import { preSendChecks } from "../../lib/leads-sample";
import {
  card,
  ContactStatusBadge,
  EmailPreview,
  EngagementStrip,
  Panel,
  SampleBanner,
  SendChecks,
  Timeline,
  when,
} from "./shared";

const STATUS: Record<MessageStatus, { label: string; className: string }> = {
  draft: { label: "To approve", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  sent_back: { label: "Sent back", className: "bg-paper text-ink ring-line" },
  sent: { label: "Sent", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  bounced: { label: "Bounced", className: "bg-red-50 text-red-700 ring-red-200" },
};

type Tab = MessageStatus | "linkedin";

const TABS: { id: Tab; label: string }[] = [
  { id: "draft", label: "To approve" },
  { id: "sent_back", label: "Sent back" },
  { id: "sent", label: "Sent" },
  { id: "bounced", label: "Bounced" },
  { id: "linkedin", label: "LinkedIn to do" },
];

const ProviderPill = () => {
  const settings = useSendingSettings();
  if (!settings.data || settings.data.provider.connected) return null;
  return (
    <Link
      to="/leads/sending"
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 ring-1 ring-amber-200"
    >
      <FlaskConical className="size-3.5" /> Test mode: no sending provider connected
    </Link>
  );
};

export const OutreachPage = () => {
  const { brand } = useActive();
  const messages = useOutreach();
  const people = useLeadPeople();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "draft";

  const linkedinToDo = (people.data ?? []).filter(
    (person) => person.bestChannel === "linkedin" && !person.linkedin.requestSentAt,
  );
  const count = (id: Tab) =>
    id === "linkedin"
      ? linkedinToDo.length
      : (messages.data ?? []).filter((message) => message.status === id).length;
  const rows = (messages.data ?? []).filter((message) => message.status === tab);

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <PageHeader
        eyebrow={`${brand?.name ?? ""} · Leads`}
        title="Outreach"
        actions={<ProviderPill />}
      >
        Every email is written for one person and approved by a person before it goes, from a real
        person's address. LinkedIn stays by hand.
      </PageHeader>
      {messages.isError && <ErrorText>{apiErrorMessage(messages.error)}</ErrorText>}
      {messages.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {messages.data && (
        <>
          <div
            role="tablist"
            aria-label="Outreach"
            className="flex flex-wrap gap-1 rounded-lg bg-paper p-1"
          >
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                onClick={() => setParams({ tab: item.id }, { replace: true })}
                className={cn(
                  "rounded-md px-3 py-1 text-xs font-medium",
                  tab === item.id ? "bg-white text-ink shadow-sm" : "text-muted hover:text-ink",
                )}
              >
                {item.label} <span className="tabular-nums text-muted">{count(item.id)}</span>
              </button>
            ))}
          </div>
          {tab === "linkedin" ? (
            <ul className={cn(card, "divide-y divide-line")}>
              {linkedinToDo.length === 0 && (
                <li className="p-6 text-sm text-muted">Nobody waiting for a LinkedIn request.</li>
              )}
              {linkedinToDo.map((person) => (
                <li
                  key={person.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div>
                    <Link
                      to={`/leads/people/${person.id}`}
                      className="font-medium text-ink hover:underline"
                    >
                      {person.name}
                    </Link>
                    <p className="text-xs text-muted">
                      {person.title}, {person.accountName}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <ContactStatusBadge status={person.contactStatus} />
                    <span className="text-xs text-muted">
                      Email isn't allowed, so LinkedIn by hand
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : rows.length === 0 ? (
            <p className={cn(card, "p-6 text-sm text-muted")}>
              {tab === "draft"
                ? "No emails waiting. Write one from a person's card or from the weekly list."
                : "Nothing here."}
            </p>
          ) : (
            <ul className={cn(card, "divide-y divide-line")}>
              {rows.map((message) => (
                <MessageRow key={message.id} message={message} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

const MessageRow = ({ message }: { message: OutreachMessageDto }) => (
  <li>
    <Link
      to={`/leads/outreach/${message.id}`}
      className="grid gap-2 px-4 py-3 hover:bg-paper/30 sm:grid-cols-[14rem_minmax(0,1fr)_auto]"
    >
      <div className="min-w-0">
        <p className="truncate font-medium text-ink">{message.personName}</p>
        <p className="truncate text-xs text-muted">{message.accountName}</p>
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm text-ink">{message.subject}</p>
        <p className="truncate text-xs text-muted">
          {message.kind === "follow_up" ? "Follow-up · " : ""}
          {message.status === "sent_back" && message.sendBackNote
            ? `Sent back: ${message.sendBackNote}`
            : (message.body.split("\n").find((line) => line.trim() && !line.startsWith("Hi")) ??
              "")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {message.testMode && (
          <Badge className="bg-amber-50 text-amber-800 ring-amber-200">Test mode</Badge>
        )}
        {message.status === "sent" && <EngagementStrip engagement={message.engagement} compact />}
        <span className="text-xs text-muted">{when(message.sentAt ?? message.createdAt)}</span>
      </div>
    </Link>
  </li>
);

// ─── One email ───────────────────────────────────────────────────────────────────────────────

export const MessagePage = () => {
  const { id = "" } = useParams();
  const message = useOutreachMessage(id);
  const data = message.data;
  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <Link
        to="/leads/outreach"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Outreach
      </Link>
      {message.isError && <ErrorText>{apiErrorMessage(message.error)}</ErrorText>}
      {message.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data &&
        (data.status === "draft" || data.status === "sent_back" ? (
          <Composer message={data} />
        ) : (
          <SentMessage message={data} />
        ))}
    </section>
  );
};

const MessageHeader = ({ message }: { message: OutreachMessageDto }) => (
  <header className="flex flex-wrap items-start justify-between gap-3">
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">
        {message.kind === "follow_up" ? "Follow-up" : "First email"} · {message.accountName}
      </p>
      <h1 className="font-serif text-3xl text-ink">
        To{" "}
        <Link to={`/leads/people/${message.personId}`} className="hover:underline">
          {message.personName}
        </Link>
      </h1>
    </div>
    <div className="flex items-center gap-2">
      {message.testMode && (
        <Badge className="bg-amber-50 text-amber-800 ring-amber-200">Test mode</Badge>
      )}
      <Badge className={STATUS[message.status].className}>{STATUS[message.status].label}</Badge>
    </div>
  </header>
);

const Composer = ({ message }: { message: OutreachMessageDto }) => {
  const navigate = useNavigate();
  const { atLeast } = useActive();
  const settings = useSendingSettings();
  const person = useLeadPerson(message.personId);
  const update = useUpdateMessage();
  const send = useSendMessage();
  const sendBack = useSendBack();
  const remove = useDeleteDraft();
  const [subject, setSubject] = useState(message.subject);
  const [body, setBody] = useState(message.body);
  const [senderId, setSenderId] = useState(message.senderId);
  const [sendingBack, setSendingBack] = useState(false);
  useEffect(() => {
    setSubject(message.subject);
    setBody(message.body);
    setSenderId(message.senderId);
  }, [message.id, message.subject, message.body, message.senderId]);

  const sender = settings.data?.senders.find((item) => item.id === senderId);
  const dirty =
    subject !== message.subject || body !== message.body || senderId !== message.senderId;
  const checks = useMemo(
    () =>
      person.data && settings.data
        ? preSendChecks(
            { subject, body, claimsUsed: message.claimsUsed },
            person.data,
            sender,
            settings.data,
          )
        : [],
    [person.data, settings.data, subject, body, message.claimsUsed, sender],
  );
  const blocked = checks.some((item) => item.state === "fail");
  const testMode = !settings.data?.provider.connected;
  const words = body.split(/\s+/).filter(Boolean).length;
  const error = update.error ?? send.error ?? sendBack.error ?? remove.error;

  const approveAndSend = async () => {
    if (dirty) await update.mutateAsync({ id: message.id, subject, body, senderId });
    await send.mutateAsync(message.id);
  };

  return (
    <>
      <MessageHeader message={message} />
      {message.status === "sent_back" && message.sendBackNote && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Sent back: “{message.sendBackNote}” Edit it and save to put it back in the queue.
        </p>
      )}
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <Panel title="Write" about="Plain words, one person, one question. Only approved claims.">
            <Field label="From">
              {(props) => (
                <select
                  {...props}
                  className={selectClass}
                  value={senderId}
                  onChange={(event) => setSenderId(event.target.value)}
                >
                  {!sender && <option value="">Choose a sender</option>}
                  {settings.data?.senders.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} &lt;{item.email}&gt;
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Subject" counter={{ value: subject.length, max: 80 }}>
              {(props) => (
                <Input
                  {...props}
                  value={subject}
                  maxLength={150}
                  onChange={(event) => setSubject(event.target.value)}
                />
              )}
            </Field>
            <Field
              label="Email"
              hint={`${words} words. The signature and opt-out line are added for you.`}
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={14}
                  value={body}
                  maxLength={5000}
                  onChange={(event) => setBody(event.target.value)}
                  style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
                />
              )}
            </Field>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              Claims it relies on:
              {message.claimsUsed.map((code) => (
                <Badge key={code} className="bg-emerald-50 text-emerald-700 ring-emerald-200">
                  {code}
                </Badge>
              ))}
              <Link to="/claims" className="ml-1 underline">
                Claims
              </Link>
            </div>
            {dirty && (
              <Button
                variant="secondary"
                disabled={update.isPending || !subject.trim() || !body.trim()}
                onClick={() => update.mutate({ id: message.id, subject, body, senderId })}
              >
                <Save className="size-4" /> Save changes
              </Button>
            )}
          </Panel>
        </div>
        <div className="min-w-0 space-y-4">
          <EmailPreview sender={sender} to={message.to} subject={subject} body={body} />
          <Panel title="Before it goes" about="Checked as you type. Anything red blocks sending.">
            {person.isPending ? (
              <LoaderCircle className="size-4 animate-spin text-muted" aria-label="Loading" />
            ) : (
              <SendChecks checks={checks} />
            )}
          </Panel>
          {error && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
          {atLeast("REVIEWER") && message.status === "draft" && (
            <div className="flex flex-wrap gap-2">
              <ConfirmDialog
                trigger={
                  <Button disabled={blocked || send.isPending || update.isPending}>
                    {send.isPending ? (
                      <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                      <Send className="size-4" />
                    )}
                    {testMode ? "Approve and send (test mode)" : "Approve and send"}
                  </Button>
                }
                title={`Send to ${message.personName}?`}
                description={
                  testMode
                    ? "No sending provider is connected, so this is recorded as sent in test mode. Nothing leaves the platform."
                    : `This sends from ${sender?.email ?? "the chosen sender"} now.`
                }
                confirmLabel={testMode ? "Record in test mode" : "Send now"}
                onConfirm={() => void approveAndSend()}
              />
              <Button variant="secondary" onClick={() => setSendingBack(true)}>
                <Undo2 className="size-4" /> Send back
              </Button>
              <ConfirmDialog
                trigger={
                  <Button variant="ghost">
                    <Trash2 className="size-4" /> Delete
                  </Button>
                }
                title="Delete this draft?"
                description="The draft goes; the person stays in your list."
                confirmLabel="Delete"
                destructive
                onConfirm={() =>
                  remove.mutate(message.id, { onSuccess: () => navigate("/leads/outreach") })
                }
              />
            </div>
          )}
        </div>
      </div>
      <PromptDialog
        open={sendingBack}
        onOpenChange={setSendingBack}
        title="Send it back"
        label="What should change?"
        placeholder="e.g. Shorter, and end with one question"
        submitLabel="Send back"
        onSubmit={(value) => {
          if (value.length < 3) return "Say what should change.";
          sendBack.mutate({ id: message.id, note: value });
          return null;
        }}
      />
    </>
  );
};

const SentMessage = ({ message }: { message: OutreachMessageDto }) => {
  const settings = useSendingSettings();
  const person = useLeadPerson(message.personId);
  const sender = settings.data?.senders.find((item) => item.id === message.senderId);
  const events = (person.data?.events ?? []).filter((item) => item.messageId === message.id);
  return (
    <>
      <MessageHeader message={message} />
      <p className="text-sm text-muted">
        {message.status === "bounced" ? "Bounced" : "Sent"}{" "}
        {message.sentAt && formatDate(message.sentAt)} as {sender?.name ?? "the sender"}, approved
        by {message.sentBy}
        {message.testMode ? ", in test mode. Nothing left the platform." : "."}
      </p>
      <div className="grid gap-6 xl:grid-cols-2">
        <EmailPreview
          sender={sender}
          to={message.to}
          subject={message.subject}
          body={message.body}
        />
        <div className="space-y-4">
          <Panel
            title="What happened"
            about="People only in the score. Machine events are hidden until you ask."
          >
            <EngagementStrip engagement={message.engagement} scoring={settings.data?.scoring} />
            <Timeline events={events} scoring={settings.data?.scoring} />
          </Panel>
        </div>
      </div>
    </>
  );
};
